import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Select, Input, Modal, Popover, Segmented, InputNumber, Switch } from "antd";
import { SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { performLogout } from "../../utils/logout";
import { apiProductosInstance, authHeaderProductos } from "../../redux/actions/productos/productos";

import { getEmpresaConfig } from "../../components/utils/empresaConfig";
import logoFull from "../../assets/img/logo-full.png";

import "../../assets/css/cajero.css";

import CashDenominations from "../../components/cajero/CashDenominations";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PaymentModal({ open, totalDue, onCancel, onConfirm, loading }) {
  const [method, setMethod] = useState("cash");
  const [received, setReceived] = useState(0);
  const [customVal, setCustomVal] = useState(null);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (open) {
      setMethod("cash");
      setReceived(0);
      setCustomVal(null);
      setCounts({});
    }
  }, [open]);

  const change = Math.max(received - totalDue, 0);
  const falta = Math.max(totalDue - received, 0);
  const canConfirm = method !== "cash" || received >= totalDue;

  const addChip = (v) => setReceived((r) => r + v);
  const pickPiece = (v) => {
    addChip(v);
    setCounts((c) => ({ ...c, [v]: (c[v] || 0) + 1 }));
  };
  const limpiar = () => { setReceived(0); setCounts({}); };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      className="cajero-modal-dark"
      title="Cobrar venta"
      width="min(1080px, 96vw)"
    >
      <div className={`pay-layout ${method === "cash" ? "pay-layout--cash" : ""}`}>
        {method === "cash" && (
          <div className="pay-pieces">
            <CashDenominations counts={counts} onPick={pickPiece} />
          </div>
        )}

        <div className="pay-side">
        <div style={{ fontSize: 22, fontWeight: 900, color: "#fff" }}>Total a cobrar: {money(totalDue)}</div>

        <Segmented
          value={method}
          onChange={setMethod}
          options={[
            { label: "Efectivo", value: "cash" },
            { label: "Tarjeta", value: "card" },
            { label: "Transferencia", value: "digital" },
          ]}
          block
          size="large"
          className="cajero-segmented"
        />

        {method === "cash" && (
          <>
            <div style={{ display: "flex", gap: 8 }}>
              <InputNumber
                style={{ flex: 1 }}
                placeholder="Otro monto"
                min={0}
                value={customVal}
                onChange={setCustomVal}
                onPressEnter={() => { if (customVal) { addChip(customVal); setCustomVal(null); } }}
              />
              <Button onClick={() => { if (customVal) { addChip(customVal); setCustomVal(null); } }}>Agregar</Button>
              <Button danger onClick={limpiar}>Limpiar</Button>
            </div>
            <div className="pay-summary">
              <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontWeight: 800, fontSize: 18 }}>
                <span>Recibido</span><span>{money(received)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", color: falta > 0 ? "#f59e0b" : "#22c55e", fontWeight: 900, fontSize: 22 }}>
                <span>{falta > 0 ? "Falta" : "Cambio"}</span><span>{money(falta > 0 ? falta : change)}</span>
              </div>
            </div>
          </>
        )}

        <Button
          type="primary"
          block
          size="large"
          className="pay-confirm"
          disabled={!canConfirm}
          loading={loading}
          onClick={() => onConfirm({ method, monto: method === "cash" ? totalDue : totalDue, cambio: method === "cash" ? change : 0, recibido: method === "cash" ? received : totalDue })}
          style={{ background: "#f90336", borderColor: "#f90336", height: 48 }}
        >
          {method === "cash" && falta > 0 ? `Falta ${money(falta)}` : "Confirmar cobro"}
        </Button>
        </div>
      </div>
    </Modal>
  );
}

function CopiasModal({ open, tarifas, onCancel, onConfirm }) {
  const [tipoCopia, setTipoCopia] = useState("blanco_negro");
  const [tamano, setTamano] = useState("carta");
  const [hojas, setHojas] = useState(1);

  const tarifa = useMemo(() => {
    const candidatas = tarifas.filter((t) => t.tipo_copia === tipoCopia && t.tamano === tamano && hojas >= t.hojas_desde && (t.hojas_hasta == null || hojas <= t.hojas_hasta));
    return candidatas[0] || null;
  }, [tarifas, tipoCopia, tamano, hojas]);

  const precioUnitario = tarifa ? parseFloat(tarifa.precio_unitario) : 0;
  const total = precioUnitario * (hojas || 0);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={() => {
        if (!tarifa) return toast.warning("No hay tarifa para esa combinacion de tipo/tamano/hojas");
        onConfirm({ tipoCopia, tamano, hojas, precioUnitario, total });
      }}
      okText="Agregar al carrito"
      cancelText="Cancelar"
      title="Fotocopias"
      className="cajero-modal-dark"
      centered
      okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Select value={tipoCopia} onChange={setTipoCopia} options={[{ label: "Blanco y negro", value: "blanco_negro" }, { label: "Color", value: "color" }]} />
        <Select value={tamano} onChange={setTamano} options={[{ label: "Carta", value: "carta" }, { label: "Oficio", value: "oficio" }]} />
        <InputNumber style={{ width: "100%" }} min={1} value={hojas} onChange={setHojas} placeholder="Numero de hojas" />
        <div style={{ fontWeight: 800, color: tarifa ? "#fff" : "#f59e0b" }}>
          {tarifa ? `${money(precioUnitario)} por hoja - Total: ${money(total)}` : "Sin tarifa configurada para esta combinacion"}
        </div>
      </div>
    </Modal>
  );
}

export default function Cajero() {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("productos");
  const [search, setSearch] = useState("");

  const [servicios, setServicios] = useState([]);
  const [tarifas, setTarifas] = useState([]);

  const [carrito, setCarrito] = useState([]);

  const [clientes, setClientes] = useState([]);
  const [idCliente, setIdCliente] = useState(null);
  const [esCredito, setEsCredito] = useState(false);

  const [copiasServicio, setCopiasServicio] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [ticket, setTicket] = useState(null);

  const [userOpen, setUserOpen] = useState(false);
  const usuario = localStorage.getItem("usuario") || "—";
  const initials = (usuario[0] || "G").toUpperCase() + (usuario[1] || "J").toUpperCase();
  const [userAvatar, setUserAvatar] = useState(() => localStorage.getItem("userAvatar") || "");
  useEffect(() => {
    const sync = (e) => setUserAvatar(e.detail || localStorage.getItem("userAvatar") || "");
    window.addEventListener("avatar-updated", sync);
    return () => window.removeEventListener("avatar-updated", sync);
  }, []);

  const [permitirSinExistencia, setPermitirSinExistencia] = useState(true);
  useEffect(() => {
    apiProductosInstance.get("/general/pos-config", { headers: authHeaderProductos() })
      .then((r) => setPermitirSinExistencia(r.data?.permitir_venta_sin_existencia !== false))
      .catch(() => {});
  }, []);

  const [empresa, setEmpresa] = useState({ nombre: "", logoDataUrl: null });
  useEffect(() => {
    getEmpresaConfig().then(setEmpresa);
  }, []);

  useEffect(() => {
    const headers = authHeaderProductos();
    apiProductosInstance.get("/servicios", { headers }).then((r) => setServicios(r.data?.items ?? [])).catch(() => {});
    apiProductosInstance.get("/copias-tarifas", { headers }).then((r) => setTarifas(r.data?.items ?? [])).catch(() => {});
    apiProductosInstance.get("/clientes", { headers }).then((r) => setClientes((r.data?.items ?? []).map((c) => ({ label: c.nombre, value: c.id_cliente })))).catch(() => {});
  }, []);

  const [variantesBuscadas, setVariantesBuscadas] = useState([]);
  useEffect(() => {
    apiProductosInstance.get("/productos/variantes/buscar", { headers: authHeaderProductos(), params: { search: search || undefined } })
      .then((r) => setVariantesBuscadas(r.data?.items ?? []))
      .catch(() => {});
  }, [search]);

  const serviciosFiltrados = useMemo(() => {
    if (!search) return servicios;
    const q = search.toLowerCase();
    return servicios.filter((s) => s.nombre.toLowerCase().includes(q));
  }, [servicios, search]);

  // Existencia insuficiente: avisa sin bloquear, salvo que Configuracion lo prohiba.
  const avisoExistencia = (nombre, disponible) => {
    const msg = `${nombre}: solo hay ${disponible} en sistema`;
    if (permitirSinExistencia) { toast.warning(msg); return true; }
    toast.error(`${msg}. La venta sin existencia esta bloqueada`);
    return false;
  };

  const addProducto = (v) => {
    const disponible = parseFloat(v.existencia);
    const existing = carrito.find((l) => l.tipo_linea === "producto" && l.id_ref === v.id_variante);
    const cantidadNueva = (existing ? existing.cantidad : 0) + 1;
    if (cantidadNueva > disponible && !avisoExistencia(v.producto_nombre, disponible)) return;
    setCarrito((prev) => {
      const existing = prev.find((l) => l.tipo_linea === "producto" && l.id_ref === v.id_variante);
      if (existing) {
        return prev.map((l) => (l === existing ? { ...l, cantidad: l.cantidad + 1 } : l));
      }
      return [...prev, {
        key: Date.now() + Math.random(),
        tipo_linea: "producto",
        id_ref: v.id_variante,
        nombre: `${v.producto_nombre} (${v.clave})`,
        cantidad: 1,
        precio: parseFloat(v.precio_venta),
        existenciaMax: parseFloat(v.existencia),
      }];
    });
  };

  const addServicio = (s) => {
    if (s.usa_tarifa_copias) {
      setCopiasServicio(s);
      return;
    }
    setCarrito((prev) => [...prev, {
      key: Date.now() + Math.random(),
      tipo_linea: "servicio",
      id_ref: s.id_servicio,
      nombre: s.nombre,
      cantidad: 1,
      precio: s.precio_base != null ? parseFloat(s.precio_base) : 0,
    }]);
  };

  const addCopias = ({ tipoCopia, tamano, hojas, precioUnitario, total }) => {
    setCarrito((prev) => [...prev, {
      key: Date.now() + Math.random(),
      tipo_linea: "servicio",
      id_ref: copiasServicio.id_servicio,
      nombre: `${copiasServicio.nombre} (${tipoCopia === "color" ? "Color" : "B/N"}, ${tamano}, ${hojas}h)`,
      cantidad: 1,
      precio: total,
      unitarioInfo: precioUnitario,
    }]);
    setCopiasServicio(null);
  };

  const updateCantidad = (key, delta) => {
    const linea = carrito.find((l) => l.key === key);
    if (!linea) return;
    const next = Math.max(linea.cantidad + delta, 0.001);
    if (delta > 0 && linea.existenciaMax != null && next > linea.existenciaMax && !avisoExistencia(linea.nombre, linea.existenciaMax)) return;
    setCarrito((prev) => prev.map((l) => (l.key === key ? { ...l, cantidad: next } : l)));
  };

  const removeLinea = (key) => {
    const linea = carrito.find((l) => l.key === key);
    setCarrito((prev) => prev.filter((l) => l.key !== key));
    if (linea) {
      toast.info(`${linea.nombre} eliminado`, {
        delay: 6000,
        action: { label: "Deshacer", onClick: () => setCarrito((prev) => [...prev, linea]) },
      });
    }
  };

  const total = carrito.reduce((a, l) => a + l.cantidad * l.precio, 0);

  const resetVenta = () => {
    setCarrito([]);
    setIdCliente(null);
    setEsCredito(false);
  };

  const buildPayload = (pagos) => ({
    id_cliente: idCliente,
    es_credito: esCredito,
    detalle: carrito.map((l) => ({
      tipo_linea: l.tipo_linea,
      id_variante: l.tipo_linea === "producto" ? l.id_ref : null,
      id_servicio: l.tipo_linea === "servicio" ? l.id_ref : null,
      descripcion: l.nombre,
      cantidad: l.cantidad,
      precio_cobrado: l.precio,
    })),
    pagos,
  });

  const handleCobrarCredito = async () => {
    if (carrito.length === 0) return toast.warning("Agrega productos o servicios al carrito");
    if (!idCliente) return toast.warning("Selecciona el cliente para vender a credito");
    setPaying(true);
    try {
      const { data } = await apiProductosInstance.post("/ventas", buildPayload([]), { headers: authProdHeaders() });
      setTicket({ id: data.id, total, cambio: 0 });
      resetVenta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar la venta");
    } finally {
      setPaying(false);
    }
  };

  const authProdHeaders = () => authHeaderProductos();

  const handleConfirmPay = async ({ monto, cambio }) => {
    setPaying(true);
    try {
      const { data } = await apiProductosInstance.post("/ventas", buildPayload([{ id_forma_pago: 1, monto }]), { headers: authProdHeaders() });
      setTicket({ id: data.id, total, cambio });
      setPayOpen(false);
      resetVenta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar la venta");
    } finally {
      setPaying(false);
    }
  };

  const confirmarSalir = () => {
    setUserOpen(false);
    Modal.confirm({
      title: "Salir del programa",
      content: carrito.length > 0
        ? "Hay una venta en curso que se perdera. ¿Estas segura de que quieres salir?"
        : "¿Estas segura de que quieres salir del programa?",
      okText: "Si, salir",
      cancelText: "Cancelar",
      centered: true,
      className: "cajero-modal-dark",
      okButtonProps: { style: { background: "#f90336", borderColor: "#f90336" } },
      onOk: () => performLogout(),
    });
  };

  const userCard = (
    <div className="pos-user-pop">
      <div className="pos-user-pop-avatar">{userAvatar ? <img src={userAvatar} alt="avatar" /> : initials}</div>
      <div className="pos-user-pop-email">{usuario}</div>
      <div className="pos-user-pop-actions">
        <button className="pos-user-pop-btn" onClick={() => navigate("/app/home")}>Volver al panel</button>
        <button className="pos-user-pop-btn danger" onClick={confirmarSalir}>Cerrar sesion</button>
      </div>
    </div>
  );

  return (
    <div className="pos-shell">
      <div className="pos-topbar">
        <div className="pos-topbar-left">
          <div className="pos-topbar-badge">
            <img src={empresa.logoDataUrl || logoFull} alt={empresa.nombre || "Gigante Jr."} />
          </div>
          <div className="pos-topbar-title">
            <div className="pos-topbar-h1">{empresa.nombre || "Gigante Jr."}</div>
            <div className="pos-topbar-sub">Punto de venta</div>
          </div>
        </div>
        <div className="pos-topbar-search">
          <SearchOutlined className="pos-search-icon" />
          <Input
            className="pos-search-input"
            placeholder="Buscar producto o servicio..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="pos-topbar-right">
          <Button className="btn-salir" icon={<i className="fa-solid fa-arrow-left" />} onClick={confirmarSalir}>
            Salir
          </Button>
        </div>
      </div>

      <div className="pos-main">
        <div className="pos-nav">
          <button className={`pos-nav-btn ${activeView === "productos" ? "pos-nav-btn-active" : ""}`} onClick={() => setActiveView("productos")}>
            <div className="pos-nav-icon"><i className="fa-solid fa-compact-disc" /></div>
            <div className="pos-nav-label">Productos</div>
          </button>
          <button className={`pos-nav-btn ${activeView === "servicios" ? "pos-nav-btn-active" : ""}`} onClick={() => setActiveView("servicios")}>
            <div className="pos-nav-icon"><i className="fa-solid fa-print" /></div>
            <div className="pos-nav-label">Servicios</div>
          </button>

          <div className="pos-nav-footer">
            <Popover content={userCard} trigger="click" open={userOpen} onOpenChange={setUserOpen} overlayClassName="pos-user-popover" placement="rightBottom">
              <div className="pos-nav-user" style={{ cursor: "pointer" }}>
                {userAvatar
                  ? <img className="pos-nav-user-avatar" src={userAvatar} alt="avatar" />
                  : <div className="pos-nav-user-initials">{initials}</div>}
              </div>
            </Popover>
          </div>
        </div>

        <div className="pos-center">
          <div className="pos-center-head">
            <div className="pos-center-title-row">
              <div className="pos-center-title">{activeView === "productos" ? "Productos" : "Servicios"}</div>
            </div>
          </div>

          <div className="pos-center-body">
            <div className="pos-menu-wrap">
              <div className="pos-menu-grid">
                {activeView === "productos" && variantesBuscadas.map((v) => (
                  <div key={v.id_variante} className="pos-menu-card" onClick={() => addProducto(v)}>
                    <div className="pos-menu-imgbox"><i className="fa-solid fa-compact-disc" style={{ fontSize: 30, color: "rgba(255,255,255,0.4)" }} /></div>
                    <div className="pos-menu-info">
                      <div className="pos-menu-name">{v.producto_nombre}</div>
                      <div className="pos-menu-row">
                        <span className="pos-menu-price">{money(v.precio_venta)}</span>
                        <span className="pos-menu-chip">{v.clave}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {activeView === "servicios" && serviciosFiltrados.map((s) => (
                  <div key={s.id_servicio} className="pos-menu-card" onClick={() => addServicio(s)}>
                    <div className="pos-menu-imgbox"><i className="fa-solid fa-print" style={{ fontSize: 30, color: "rgba(255,255,255,0.4)" }} /></div>
                    <div className="pos-menu-info">
                      <div className="pos-menu-name">{s.nombre}</div>
                      <div className="pos-menu-row">
                        <span className="pos-menu-price">{s.usa_tarifa_copias ? "Por hoja" : money(s.precio_base)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="pos-order">
          <div className="pos-order-head">
            <div className="pos-order-title-row">
              <div className="pos-order-title">Venta actual</div>
            </div>
            <div className="pos-order-form">
              {clientes.length > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: "#fff", fontWeight: 800, fontSize: 12 }}>Venta a credito</span>
                  <Switch
                    checked={esCredito}
                    onChange={(v) => { setEsCredito(v); if (!v) setIdCliente(null); }}
                  />
                </div>
              )}
              {esCredito && clientes.length > 0 && (
                <Select
                  style={{ width: "100%", marginTop: 10 }}
                  placeholder="Selecciona el cliente"
                  options={clientes}
                  value={idCliente}
                  onChange={setIdCliente}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  className="pos-input"
                />
              )}
            </div>
          </div>

          <div className="pos-order-items">
            {carrito.length === 0 && (
              <div className="pos-order-empty">
                <div className="pos-order-empty-icon"><i className="fa-solid fa-cart-shopping" /></div>
                <div className="pos-order-empty-text">Agrega productos o servicios</div>
              </div>
            )}
            {carrito.map((l) => (
              <div key={l.key} className="pos-item">
                <div className="pos-item-thumb"><span className="pos-item-thumb-fallback">{l.tipo_linea === "producto" ? "P" : "S"}</span></div>
                <div className="pos-item-main">
                  <div className="pos-item-top">
                    <span className="pos-item-name" title={l.existenciaMax != null && l.cantidad > l.existenciaMax ? `Solo hay ${l.existenciaMax} en sistema` : undefined}>
                      {l.nombre}
                      {l.existenciaMax != null && l.cantidad > l.existenciaMax && (
                        <i className="fa-solid fa-triangle-exclamation" style={{ color: "#f59e0b", marginLeft: 6 }} />
                      )}
                    </span>
                    <span className="pos-item-price">{money(l.cantidad * l.precio)}</span>
                  </div>
                  <div className="pos-item-actions">
                    <div className="pos-qty">
                      <button className="pos-qty-btn" onClick={() => updateCantidad(l.key, -1)}>-</button>
                      <span className="pos-qty-input">{l.cantidad}</span>
                      <button className="pos-qty-btn" onClick={() => updateCantidad(l.key, 1)}>+</button>
                    </div>
                    <div className="pos-item-iconbar">
                      <Button className="pos-icon-btn" icon={<DeleteOutlined className="pos-icon-del" />} onClick={() => removeLinea(l.key)} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="pos-order-total">
            <div className="pos-total-lines">
              <div className="pos-total-row-big">
                <span className="strong">Total</span>
                <span className="pos-total-pill">{money(total)}</span>
              </div>
            </div>
            <Button
              className="btn-enviar pos-pay"
              disabled={carrito.length === 0 || paying}
              loading={paying}
              onClick={() => (esCredito ? handleCobrarCredito() : setPayOpen(true))}
            >
              {esCredito ? "Registrar venta a credito" : "Cobrar"}
            </Button>
          </div>
        </div>
      </div>

      <PaymentModal open={payOpen} totalDue={total} onCancel={() => setPayOpen(false)} onConfirm={handleConfirmPay} loading={paying} />

      <CopiasModal
        open={Boolean(copiasServicio)}
        tarifas={tarifas}
        onCancel={() => setCopiasServicio(null)}
        onConfirm={addCopias}
      />

      <Modal
        open={Boolean(ticket)}
        onCancel={() => setTicket(null)}
        footer={<Button type="primary" onClick={() => setTicket(null)} style={{ background: "#f90336", borderColor: "#f90336" }}>Cerrar</Button>}
        centered
        className="cajero-modal-dark"
        title="Venta registrada"
      >
        {ticket && (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>Folio de venta</div>
            <div style={{ fontSize: 32, fontWeight: 900, color: "var(--color-secondary)" }}>#{ticket.id}</div>
            <div style={{ marginTop: 10, fontSize: 18, fontWeight: 800, color: "#fff" }}>Total: {money(ticket.total)}</div>
            {ticket.cambio > 0 && <div style={{ marginTop: 6, fontSize: 20, fontWeight: 900, color: "#22c55e" }}>Cambio: {money(ticket.cambio)}</div>}
          </div>
        )}
      </Modal>
    </div>
  );
}
